import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';
import { DEGEN_PRIZE_CURRENCY } from '../../enum-registry.ts';
import { parseIsoToEpochMs } from '../../../time-utils.ts';
import {
  type DegenPrizeCurrency,
  parseDegenPrizeCurrency,
} from '../../../master-data/types/degen-season.types.ts';

export const DegenSeasonSchema = defineSchema({
  name: 'serverDegenSeasons',
  fields: [
    ['id', 'number'],
    ['startTime', 'number'], // epoch ms (was 'string')
    ['endTime', 'number'], // epoch ms (was 'string')
    // append-only (튜플 와이어 순서 계약). 난이도별 몬스터 테이블 FK = degen_league row id.
    ['nornsTableId', 'number'],
    ['jotnarTableId', 'number'],
    ['ragnarokTableId', 'number'],
    // v1 시즌 랭킹: 페이즈 경계(분) + 상금풀(NCG)
    ['warmupDurationMin', 'number'],
    ['competeDurationMin', 'number'],
    ['boostDurationMin', 'number'],
    ['claimDurationMin', 'number'],
    ['prizePoolBaseNcg', 'number'],
    ['prizePoolNcgPerTicket', 'number'],
    // 상금풀 통화 택1 + 글로리 금액. 슬롯 0 = 'ncg' 라 재시딩된 레거시 행은 기존 NCG
    // 동작 그대로다. 단, **아직 재시딩되지 않은 12슬롯 튜플**은 decode 시 이 세 값이
    // undefined 로 나온다(generic-codec 은 부족 슬롯을 채우지 않는다) — 소비 경계에서
    // toDegenPrizeCurrency()/?? 0 으로 접는다(서버 getSeasonMeta 참조).
    [
      'prizePoolCurrency',
      { kind: 'enum', name: 'DegenPrizeCurrency', members: DEGEN_PRIZE_CURRENCY },
    ],
    ['prizePoolBaseGlory', 'number'],
    ['prizePoolGloryPerTicket', 'number'],
  ],
  lookupKeys: ['id'],
});

export interface RawDegenSeasonRow {
  id: number;
  start_time: string;
  end_time: string;
  norns_table_id: number;
  jotnar_table_id: number;
  ragnarok_table_id: number;
  warmup_duration_min?: number;
  compete_duration_min?: number;
  boost_duration_min?: number;
  claim_duration_min?: number;
  prize_pool_base_ncg?: number;
  prize_pool_ncg_per_ticket?: number;
  prize_pool_currency?: DegenPrizeCurrency;
  prize_pool_base_glory?: number;
  prize_pool_glory_per_ticket?: number;
}

export function degenSeasonFromRaw(row: RawDegenSeasonRow) {
  return {
    id: row.id,
    startTime: parseIsoToEpochMs(row.start_time),
    endTime: parseIsoToEpochMs(row.end_time),
    nornsTableId: row.norns_table_id,
    jotnarTableId: row.jotnar_table_id,
    ragnarokTableId: row.ragnarok_table_id,
    warmupDurationMin: row.warmup_duration_min ?? 0,
    competeDurationMin: row.compete_duration_min ?? 0,
    boostDurationMin: row.boost_duration_min ?? 0,
    claimDurationMin: row.claim_duration_min ?? 0,
    prizePoolBaseNcg: row.prize_pool_base_ncg ?? 0,
    prizePoolNcgPerTicket: row.prize_pool_ncg_per_ticket ?? 0,
    // 컬럼 부재(구 스키마)만 'ncg' 로 접고, 알 수 없는 통화 문자열은 던진다 — 조용히
    // 접으면 글로리 시즌이 "0 NCG" 로 라이브에 뜬다. DB CHECK 은 dev 에만 있고
    // sync_schema 가 CHECK 을 승급하지 않으므로 authoring env 의 실질 가드는 이쪽이다.
    prizePoolCurrency: parseDegenPrizeCurrency(row.prize_pool_currency),
    prizePoolBaseGlory: row.prize_pool_base_glory ?? 0,
    prizePoolGloryPerTicket: row.prize_pool_glory_per_ticket ?? 0,
  };
}

registerCategorySchema('serverDegenSeasons', DegenSeasonSchema);
